import React, { useState } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import { dialogCell, muiOptions } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import moment from 'moment'
import { ColumnFilterField, ColumnFilter } from '../components/table/table'
import { UserSettingsContextContainer } from '../../../infrastructure/shell/userSettings/userSettingsStore'

type Props = {
    items: TableItem[]
    classes?: any
}

export type TableItem = {
    username: string
    date: Date
    field: string
    oldValue: string
    newValue: string
    where?: string
}

type HistoryFilter =
    {
        usernameFilter: string
        dateFilter: string,
        fieldFilter: string
    }

let emptyHistoryFilter: HistoryFilter = {
    usernameFilter: '',
    dateFilter: '',
    fieldFilter: ''
}

let tBase = 'history.table.'

function _historyTable(props: Props) {
    if (props.items.length === 0)
        return (<span className={props.classes?.italic + ' ' + props.classes?.comment}>{t(tBase + 'empty')}</span>)

    let hasWhere = props.items.some(x => x.where)
    let hasValues = props.items.some(x => x.oldValue) || props.items.some(x => x.newValue)

    let [filter, setFilter] = useState<HistoryFilter>(emptyHistoryFilter)

    let settings = UserSettingsContextContainer.useContainer()
    let dateFormat = settings.getDateFormat()

    let getFilter = (filterKey: keyof HistoryFilter): ColumnFilter => ({
        value: filter[filterKey],
        onChange: (value: string) => setFilter({ ...filter, [filterKey]: value })
    })

    let applyFilter = (item: TableItem): boolean => {
        let filterResult = true
        if (filter.usernameFilter) {
            filterResult = filterResult && item.username.toLowerCase().includes(filter.usernameFilter.toLowerCase())
        }
        if (filter.dateFilter) {
            filterResult = filterResult && moment(item.date).format(`${dateFormat} HH:mm`).includes(filter.dateFilter)
        }
        if (filter.fieldFilter) {
            filterResult = filterResult && item.field.toLowerCase().includes(filter.fieldFilter.toLowerCase())
        }
        return filterResult
    }

    return (
        <TableContainer component={Paper}>
            <Table className={props.classes?.table} aria-label='history table'>
                <TableHead>
                    <TableRow>
                        <TableCell className={props.classes?.cell}>{t(tBase + 'user')}</TableCell>
                        <TableCell className={props.classes?.cell}>{t(tBase + 'date')}</TableCell>
                        <TableCell className={props.classes?.cell}>{t(tBase + 'field')}</TableCell>
                        {hasValues ? <TableCell className={props.classes?.cell}>{t(tBase + 'old')}</TableCell> : null}
                        {hasValues ? <TableCell className={props.classes?.cell}>{t(tBase + 'new')}</TableCell> : null}
                        {hasWhere ? <TableCell className={props.classes?.cell}>{t(tBase + 'where')}</TableCell> : null}
                    </TableRow>
                    <TableRow>
                        <TableCell><ColumnFilterField filter={getFilter('usernameFilter')} classes={props.classes} /></TableCell>
                        <TableCell><ColumnFilterField filter={getFilter('dateFilter')} classes={props.classes} /></TableCell>
                        <TableCell><ColumnFilterField filter={getFilter('fieldFilter')} classes={props.classes} /></TableCell>
                        {hasValues ? <TableCell className={props.classes?.cell}></TableCell> : null}
                        {hasValues ? <TableCell className={props.classes?.cell}></TableCell> : null}
                        {hasWhere ? <TableCell className={props.classes?.cell}></TableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.items.filter(applyFilter).map((item, index) => (
                        <TableRow key={index}>
                            <TableCell className={props.classes?.cell} component='th' scope='row'>
                                {item.username}
                            </TableCell>
                            <TableCell className={props.classes?.cell}>{moment(item.date).format(`${dateFormat} HH:mm`)}</TableCell>
                            <TableCell className={props.classes?.cell}>{item.field !== '*' ? item.field : 'Creation'}</TableCell>
                            {hasValues ? <TableCell className={props.classes?.cell + ' ' + props.classes?.italic}>{item.oldValue}</TableCell> : null}
                            {hasValues ? <TableCell className={props.classes?.cell + ' ' + props.classes?.italic}>{item.newValue}</TableCell> : null}
                            {hasWhere ? (<TableCell className={props.classes?.cell}>{item.where ?? ''}</TableCell>) : null}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

let styles = theme => createStyles({
    cell: dialogCell,
    italic: {
        fontStyle: 'italic'
    },
    comment: {
        color: 'silver',
        fontSize: '0.8em',
        fontFamily: 'Roboto',
        margin: '4em'
    }
})

export let HistoryTable = withStyles(styles, muiOptions)(_historyTable)